import { LandingPageContent } from '../types';

/**
 * Pillar page for the highest-volume commercial query in our set ("gifts for
 * kids" / "kids gifts"). Deliberately broad; the age- and occasion-specific
 * pages below it target the long-tail and link back here.
 */
const giftsForKids: LandingPageContent = {
	path: '/gifts-for-kids',
	seoTitle: 'Gifts for Kids — Personalized Presents They Actually Keep',
	metaDescription:
		'Thoughtful, personalized gifts for kids of every age — custom storybooks starring your child, coloring books, t-shirts and keepsakes. Made to order and shipped worldwide.',
	heading: 'Gifts for Kids They Actually Keep',
	subheading: 'Personalized presents that outlast the wrapping paper.',
	heroImage: '/assets/hero/kid-1/watercolor_scene.png',
	intro: `Most children's gifts have a shelf life of about a fortnight. The noisy plastic thing gets loved hard, then quietly migrates to the bottom of the toy box.

The gifts that survive are the ones that are *about* the child — where they see their own name, their own face, their own story. That is the entire idea behind StoriesVillage: we take a photo of your child and build them into an illustrated storybook, a coloring book, or a printed keepsake where they are unmistakably the hero.

Below is how we'd think about choosing, by age and by occasion.`,
	sections: [
		{
			heading: 'Gifts by age',
			body: `**Ages 1–2.** Keep it tactile and short. A personalized board-style story with big, bold illustrations works better than anything with a plot. At this age the win is recognition — a toddler pointing at the page and saying their own name is the whole experience.

**Ages 3–5.** The sweet spot for personalized storybooks. Children this age are old enough to follow a narrative and young enough to entirely believe they went to the moon last Tuesday. Personalized coloring books also land well, because the child gets to finish the artwork themselves.

**Ages 6–8.** Early readers. A book where the main character shares their name is a genuinely effective motivator — the reading stops being homework and starts being *about them*. Longer stories and more detailed art styles suit this age.

**Ages 9+.** Storybooks start to feel young. Printed keepsakes do better: a custom t-shirt, a poster of them illustrated as an adventurer, a mug for the hot chocolate they insist on making themselves.`,
		},
		{
			heading: 'Gifts by occasion',
			body: `**Birthdays.** The default. A personalized book stands out precisely because it can't be bought off a shelf in a rush — and it photographs beautifully at the party.

**Christmas and holidays.** Order early. Anything made to order has a production window, and December is when print queues get long everywhere.

**New sibling.** One of the strongest use cases we see. A book that casts the older child as the big brother or big sister — printed and handed over on the day the baby arrives — does real emotional work at a moment when the older child is feeling displaced.

**First day of school, moving house, a hospital stay.** Any transition where a child needs to see themselves as brave. A story where they already did the brave thing is a surprisingly effective piece of scaffolding.

**Grandparents buying from far away.** Personalized gifts travel well. They signal effort and attention in a way that a gift card cannot, which matters when you can't be there in person.`,
		},
		{
			heading: 'How personalization actually works here',
			body: `You upload a photo. We generate an illustrated avatar of your child in the art style you choose — watercolour, classic storybook, flat modern, or a Pixar-like 3D look. That avatar is then drawn consistently into every page of the story, so it reads as one character rather than a photo pasted onto a background.

You approve the artwork before anything is printed. Nothing goes to the printer until you have seen the pages and said yes.

Then it's printed properly — real paper, real binding — and shipped to your door.`,
		},
		{
			heading: 'What makes a personalized gift good rather than gimmicky',
			body: `A name printed on a mass-produced item is not really personalization; it's monogramming. The difference shows up in whether the child returns to the gift a month later.

Three things separate the two:

1. **The child is the subject, not a label.** They should appear in the artwork, not just in the text.
2. **The art is consistent.** If the character's face changes between pages, the spell breaks immediately — children notice this faster than adults do.
3. **It's physical.** A PDF is not a keepsake. Something printed, on a shelf, that can be handed to a grandparent, is.`,
		},
	],
	picks: [
		{
			label: 'Personalized storybooks',
			href: '/personalized-story-books',
			description:
				'Your child illustrated as the hero of their own printed adventure. Best for ages 2–8.',
		},
		{
			label: 'Personalized coloring books',
			href: '/personalized-coloring-books',
			description:
				'Outline pages starring your child, ready to be finished in felt-tip. Best for ages 3–7.',
		},
		{
			label: 'Custom kids t-shirts',
			href: '/custom-kids-t-shirts',
			description: 'Their illustrated character, printed and wearable. Works at any age.',
		},
		{
			label: 'Browse everything',
			href: '/products',
			description: 'The full catalogue — books, prints, mugs, posters, puzzles and more.',
		},
	],
	faqs: [
		{
			question: 'What is a good personalized gift for a child?',
			answer:
				'For ages 2–8, a personalized storybook where the child is illustrated as the main character is the strongest option — it is both a gift and a keepsake. For older children, printed items such as custom t-shirts or posters of their illustrated character age better than storybooks.',
		},
		{
			question: 'How far in advance should I order a personalized gift?',
			answer:
				'Personalized items are made to order, so allow time for artwork generation, your approval, printing and shipping. Ordering at least two to three weeks before the date is comfortable; for December, order earlier, as print queues lengthen across the industry.',
		},
		{
			question: 'Do I need a professional photo of my child?',
			answer:
				'No. A clear, well-lit photo taken on a phone works well. What matters is that the face is visible and in focus, and that it is not heavily shadowed or at an extreme angle.',
		},
		{
			question: 'Can I see the book before it is printed?',
			answer:
				'Yes. You review the generated artwork and approve every page before anything goes to print. If a page is not right, it can be regenerated.',
		},
		{
			question: 'Can I order a personalized gift in a language other than English?',
			answer:
				'Yes. Stories can be produced in several languages, including right-to-left languages such as Arabic, with the page order and layout adjusted accordingly.',
		},
	],
	related: [
		{ label: 'Personalized gifts for kids', href: '/personalized-gifts-for-kids' },
		{ label: 'Birthday gifts for kids', href: '/gifts/birthday-gifts-for-kids' },
		{ label: 'Christmas gifts for kids', href: '/gifts/christmas-gifts-for-kids' },
		{ label: 'Eid gifts for kids', href: '/gifts/eid-gifts-for-kids' },
		{ label: 'Gifts for 3-year-olds', href: '/gifts/gifts-for-3-year-olds' },
		{ label: 'Gifts for 4-year-olds', href: '/gifts/gifts-for-4-year-olds' },
		{ label: 'Gifts for 5-year-olds', href: '/gifts/gifts-for-5-year-olds' },
		{
			label: 'Gifts for a new big brother or sister',
			href: '/blog/gifts-for-new-siblings',
		},
	],
	cta: { label: 'Browse personalized gifts', href: '/products' },
};

export default giftsForKids;
